import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Select from 'react-select';
import { uniqBy } from 'lodash';
import { useDebounce } from 'use-debounce';

import { getURLWithSearchParams } from 'lib/networking/endpoints';
import { httpGet } from 'lib/networking/http';

const RESULTS_PER_PAGE = 100;

function SearchableScrollableMultiSelect({
  onChange,
  fetchUrl,
  optionMapper,
  initOptions = [],
  className = null,
  isMultiSelect = true,
  additionalParams = {},
}) {
  const [options, setOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState(initOptions);
  const [loading, setLoading] = useState(false);
  const [canFetchMore, setCanFetchMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery] = useDebounce(searchValue, 500);
  const prevAdditionalParamsRef = useRef(JSON.stringify(additionalParams));
  const prevSearchQueryRef = useRef(searchQuery);

  const loadMoreOptions = page => {
    if (!canFetchMore) {
      return;
    }
    setLoading(true);
    const url = getURLWithSearchParams(fetchUrl, {
      limit: RESULTS_PER_PAGE,
      offset: (page - 1) * RESULTS_PER_PAGE,
      search: searchQuery,
      ...additionalParams,
    });
    httpGet(url)
      .then(res => {
        if (res.status === 200) {
          const items = res.data?.data || [];
          if (items?.length) {
            setCurrentPage(page + 1);
            // Use functional update to get the latest state
            setOptions(prevOptions =>
              uniqBy([...prevOptions, ...items.map(optionMapper)], 'value'),
            );
          } else {
            setCanFetchMore(false);
          }
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setSelectedOptions(initOptions);
  }, [initOptions]);

  // Combined effect for search query and additionalParams changes
  useEffect(() => {
    const currentAdditionalParams = JSON.stringify(additionalParams);
    const searchChanged = prevSearchQueryRef.current !== searchQuery;
    const paramsChanged = prevAdditionalParamsRef.current !== currentAdditionalParams;

    // Only reset and refetch if something actually changed (not on initial mount)
    if (searchChanged || paramsChanged) {
      setCurrentPage(1);
      setOptions([]);
      setCanFetchMore(true);
      if (paramsChanged) {
        setSearchValue(''); // Clear search when params change
      }
      loadMoreOptions(1);

      // Update refs
      prevSearchQueryRef.current = searchQuery;
      prevAdditionalParamsRef.current = currentAdditionalParams;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, JSON.stringify(additionalParams)]);

  // initial load - only runs once on mount
  useEffect(() => {
    loadMoreOptions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Select
      className={className}
      isMulti={isMultiSelect}
      isClearable
      isSearchable
      isLoading={loading}
      options={options}
      value={selectedOptions}
      onChange={options => {
        setSelectedOptions(options || []);
        onChange(options);
      }}
      onMenuScrollToBottom={() => loadMoreOptions(currentPage)}
      inputValue={searchValue}
      onInputChange={input => setSearchValue(input)}
    />
  );
}

SearchableScrollableMultiSelect.propTypes = {
  onChange: PropTypes.func,
  fetchUrl: PropTypes.string,
  optionMapper: PropTypes.func,
  initOptions: PropTypes.array,
  className: PropTypes.string,
  isMultiSelect: PropTypes.bool,
  additionalParams: PropTypes.object,
};

export default SearchableScrollableMultiSelect;
